import React from 'react';
import { type Editor, type Element } from 'slate';
import type Tool from '../../tools/Tool';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import ElementToolbarSubmenu from './ElementToolbarSubmenu';
import ToolButton from '../Toolbar/ToolButton';
import ToolbarHDivider from './ToolbarHDivider';

type ToolbarTools = typeof Tool | ToolbarTools[];

interface RenderToolsProps {
  controller: AhaEditorV2Controller;
  toolbarTools: ToolbarTools[];
  editor: Editor;
  element: Element;
  toolbarShowModal: (tool: typeof Tool) => void;
  toolbarHideModal: () => void;
  tippySingleton: any;
  toolArgs?: Record<string, unknown>;
}

// Render each of the tools in the toolbar.
// NOTE: Don't wrap this component in ApplicationView: this will prevent it from re-rendering when the editor/selection changes.
const RenderTools: React.FC<RenderToolsProps> = ({
  controller,
  toolbarTools,
  editor,
  element,
  toolbarShowModal,
  toolbarHideModal,
  tippySingleton,
  toolArgs,
}) => {
  if (!toolbarTools) return null;

  const availableToolbarTools = toolbarTools
    .map((tool: typeof Tool | (typeof Tool)[]) => {
      if (Array.isArray(tool)) {
        const availableTools = tool.filter((t) =>
          t.available({ editor, controller })
        );

        if (availableTools.length > 0) {
          return availableTools;
        }
      } else if (tool.available({ editor, controller })) {
        return tool;
      }
      return null;
    })
    .filter(Boolean);

  return (
    <>
      {availableToolbarTools.map(
        (tool: typeof Tool | (typeof Tool)[], i: number) => {
          if (Array.isArray(tool)) {
            return (
              <React.Fragment key={`fragment-${i}`}>
                {i > 0 && <ToolbarHDivider key={`divider-${i}`} />}
                <RenderTools
                  controller={controller}
                  toolbarTools={tool}
                  editor={editor}
                  element={element}
                  toolbarShowModal={toolbarShowModal}
                  toolbarHideModal={toolbarHideModal}
                  tippySingleton={tippySingleton}
                  toolArgs={toolArgs}
                />
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment key={tool.toolName}>
                {tool.submenuTools ? (
                  <ElementToolbarSubmenu
                    tool={tool}
                    editor={editor}
                    element={element}
                    toolbarShowModal={toolbarShowModal}
                    toolbarHideModal={toolbarHideModal}
                    controller={controller}
                    tippySingleton={tippySingleton}
                    {...toolArgs}
                  />
                ) : (
                  <ToolButton
                    tippySingleton={tippySingleton}
                    tool={tool}
                    controller={controller}
                    editor={editor}
                    toolbarShowModal={toolbarShowModal}
                    toolbarHideModal={toolbarHideModal}
                    element={element}
                    {...toolArgs}
                  />
                )}
              </React.Fragment>
            );
          }
        }
      )}
    </>
  );
};

export default RenderTools;
